import React, { useState, useEffect } from "react";
import { Modal, Box, Typography, Button, TextField, CircularProgress, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,IconButton } from "@mui/material";
import { BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer } from "recharts";
import { useAuth } from "./TokenHandler";
import BuildingCheckInHistoryModal from "./BuildingCheckInHistoryModal";

const modalStyle = {
    position: "fixed",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: 1250,
    bgcolor: "background.paper",
    boxShadow: 24,
    p: 0,
    borderRadius: "0px",
    display: "flex",
    flexDirection: "column",
    maxHeight: "95vh", // Ensures the modal does not exceed viewport height
};


const headerStyle = {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    bgcolor: "#00A7A0",
    color: "white",
    padding: "8px 24px",
    flexShrink: 0, // Prevents it from shrinking
};


const MonthlyCheckinRunReport = ({ open, onClose }) => {
    const { keycloak: kc } = useAuth();

    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");
    const [reportData, setReportData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [buildingData, setBuildingData] = useState([]); // Store fetched buildings
    const [selectedBuilding, setSelectedBuilding] = useState(null);
    const [openModal, setOpenModal] = useState(false);

    const handleBuildingClick = (building) => {
        setSelectedBuilding(building);
        setOpenModal(true);
    };

    const handleCloseModal = () => {
        setOpenModal(false);
        setSelectedBuilding(null);
    };
    console.log('buildingData', buildingData);

    const formatDate = (dateString) => {
        const [year, month] = dateString.split("-");
        return `${parseInt(month)}-${year}`; // Converts "2025-02" → "2-2025"
    };

    const formatChartLabel = (label) => {
        const [month, year] = label.split("_");
        return `${month}/${year}`; // Converts "2_25" → "2/25"
    };

    const handleRunReport = async () => {
        if (!startDate || !endDate) {
            alert("Please select both start and end months.");
            return;
        }

        const formattedStart = formatDate(startDate);
        const formattedEnd = formatDate(endDate);

        const requestBody = {
            start_date: formattedStart,
            end_date: formattedEnd,
        };

        setLoading(true);

        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/build-report`, {
                method: "POST",
                headers: {
                    "Authorization": `Bearer ${kc.token}`,
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(requestBody),
            });

            if (response.ok) {
                const data = await response.json();
                setReportData(data);

                // Extract unique corp_unique_ids from the report data
                const uniqueCorpIds = [...new Set(data.buildings.map((b) => b.corp_unique_id))];

                // Fetch building data for each corp_unique_id
                fetchBuildingData(uniqueCorpIds);
            } else {
                console.error("Failed to fetch report");
            }
        } catch (error) {
            console.error("Error fetching report:", error);
        }

        setLoading(false);
    };

    const fetchBuildingData = async (corpIds) => {
        const allBuildings = [];
        console.log("Fetching buildings for corp IDs:", corpIds);

        for (const corpId of corpIds) {
            try {
                const response = await fetch(`${process.env.REACT_APP_API_URL}/building_mapping/${corpId}`, {
                    method: "GET",
                    headers: {
                        "Authorization": `Bearer ${kc.token}`,
                        "Content-Type": "application/json",
                    },
                });

                if (response.ok) {
                    const data = await response.json();

                    // Ensure corp_unique_id is added to each building entry
                    const buildingsWithCorpId = data.map((building) => ({
                        ...building,
                        corp_unique_id: corpId, // Apply the corpId to each building
                    }));

                    allBuildings.push(...buildingsWithCorpId);
                } else {
                    console.error("Failed to fetch building data for corp_unique_id:", corpId);
                }
            } catch (error) {
                console.error("Error fetching building data:", error);
            }
        }

        setBuildingData(allBuildings);
        console.log("Fetched building data:", allBuildings);
    };


    // Convert overall_count_by_month into an array for charting
    const chartData = reportData?.overall_count_by_month
        ? Object.entries(reportData.overall_count_by_month)
            .map(([monthYear, counts]) => ({
                month: formatChartLabel(monthYear),
                numericMonth: parseInt(monthYear.split("_")[0]), // Extract numeric month
                numericYear: parseInt(monthYear.split("_")[1]),  // Extract numeric year
                ...counts,
            }))
            .sort((a, b) => (a.numericYear - b.numericYear) || (a.numericMonth - b.numericMonth)) // Sort by year first, then month
            .map(({ numericMonth, numericYear, ...rest }) => rest) // Remove numericMonth and numericYear after sorting
        : [];


    // Convert overall_count into an array for a separate chart
    const overallChartData = reportData?.overall_count
        ? Object.entries(reportData.overall_count).map(([status, count]) => ({
            status,
            count,
        }))
        : [];
    const setDateRange = (months) => {
        const today = new Date();
        const start = new Date();
        start.setMonth(today.getMonth() - (months - 1)); // Set to the start of the range

        const formattedStart = `${start.getFullYear()}-${String(start.getMonth() + 1).padStart(2, "0")}`;
        const formattedEnd = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}`;

        setStartDate(formattedStart);
        setEndDate(formattedEnd);
    };



    return (
        <Modal open={open} onClose={onClose} aria-labelledby="run-report-modal">
            <Box sx={modalStyle}>

                {/* Fixed Header */}
                <Box sx={headerStyle}>
                    <Typography id="run-report-modal" variant="h6">
                        Monthly Check in Report Dashboard
                    </Typography>
                    <IconButton onClick={onClose} sx={{ color: "white" }}>
                        ✕
                    </IconButton>
                </Box>

                {/* Scrollable Content */}
                <Box sx={{ overflowY: "auto", flexGrow: 1, padding: "0 10px", marginTop: "25px", marginBottom: "10px" }}>
                    {/* Date Selection Row */}
                    <Box display="flex" gap={2} alignItems="center">
                        {["1M", "3M", "6M", "1Y"].map((label, index) => {
                            const months = [1, 3, 6, 12][index];
                            return (
                                <Button
                                    key={label}
                                    variant="outlined"
                                    onClick={() => setDateRange(months)}
                                    sx={{
                                        height: "56px",
                                        minWidth: "80px",
                                        borderColor: "rgba(0, 0, 0, 0.23)",
                                        color: "rgba(0, 0, 0, 0.87)",
                                        backgroundColor: "white",
                                        "&:hover": { backgroundColor: "#f0f0f0" },
                                    }}
                                >
                                    {label}
                                </Button>
                            );
                        })}

                        <TextField
                            label="Start Month"
                            type="month"
                            variant="standard"
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                            sx={{
                                backgroundColor: "#F9F9F9",
                                "& .MuiOutlinedInput-root": {
                                    "& fieldset": {
                                        borderColor: "#CCCCCC",
                                    },
                                    "&:hover fieldset": {
                                        borderColor: "#F9F9F9",
                                    },
                                    "&.Mui-focused fieldset": {
                                        borderColor: "#F9F9F9",
                                    },
                                },
                                "& .MuiInputLabel-root": {
                                    color: "#666666",
                                },
                                "& .MuiInputLabel-shrink": {
                                    color: "#666666",
                                },
                            }}
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                        />

                        <TextField
                            label="End Month"
                            type="month"
                            variant="standard"
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                            sx={{
                                backgroundColor: "#F9F9F9",
                                "& .MuiOutlinedInput-root": {
                                    "& fieldset": {
                                        borderColor: "#CCCCCC",
                                    },
                                    "&:hover fieldset": {
                                        borderColor: "#F9F9F9",
                                    },
                                    "&.Mui-focused fieldset": {
                                        borderColor: "#F9F9F9",
                                    },
                                },
                                "& .MuiInputLabel-root": {
                                    color: "#666666",
                                },
                                "& .MuiInputLabel-shrink": {
                                    color: "#666666",
                                },
                            }}
                            value={endDate}
                            onChange={(e) => setEndDate(e.target.value)}
                        />


                    </Box>

                    {/* Run Report Button */}
                    <Box mt={3} display="flex" justifyContent="flex-end">
                        <Button onClick={handleRunReport} variant="contained" color="primary" disabled={loading}>
                            {loading ? <CircularProgress size={24} /> : "Run Report"}
                        </Button>
                    </Box>

                    {/* Report Content (Charts & Tables) */}
                    {reportData && (
                        <Box mt={4}>
                            <Typography variant="h6">Overall Count</Typography>
                            <ResponsiveContainer width="100%" height={300}>
                                <BarChart data={overallChartData}>
                                    <XAxis dataKey="status" />
                                    <YAxis />
                                    <Tooltip />
                                    <Legend />
                                    <Bar dataKey="count" fill="#8884d8" />
                                </BarChart>
                            </ResponsiveContainer>

                            <Typography variant="h6" mt={4}>Monthly Breakdown</Typography>
                            <ResponsiveContainer width="100%" height={300}>
                                <BarChart data={chartData}>
                                    <XAxis dataKey="month" />
                                    <YAxis />
                                    <Tooltip />
                                    <Legend />
                                    <Bar dataKey="Held and attended" fill="#4CAF50" />
                                    <Bar dataKey="Held and attended - Corp" fill="#8BC34A" />
                                    <Bar dataKey="Customer no show" fill="#F44336" />
                                    <Bar dataKey="Scheduled" fill="#2196F3" />
                                    <Bar dataKey="Scheduled - Corp" fill="#FFC107" />
                                </BarChart>
                            </ResponsiveContainer>

                            {/* Table */}
                            <Typography variant="h6" mt={4}>Detailed Report</Typography>
                            <TableContainer component={Paper} sx={{ mt: 2, maxHeight: 400 }}>
                                <Table stickyHeader>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell><strong>Building Name</strong></TableCell>
                                            <TableCell><strong>Month/Year</strong></TableCell>
                                            <TableCell><strong>Held and attended</strong></TableCell>
                                            <TableCell><strong>Held and attended - Corp</strong></TableCell>
                                            <TableCell><strong>Customer no show</strong></TableCell>
                                            <TableCell><strong>Scheduled</strong></TableCell>
                                            <TableCell><strong>Scheduled - Corp</strong></TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {reportData.buildings.map((building) => {
                                            const matchedBuilding = buildingData.find(
                                                (b) => b.building_unique_id === building.building_unique_id &&
                                                    b.corp_unique_id === building.corp_unique_id
                                            );
                                            const buildingName = matchedBuilding ? matchedBuilding.legal_name : "Unknown";

                                            return (
                                                <React.Fragment key={building.building_unique_id}>
                                                    {/* Building Name Row - Spanning all columns */}
                                                    <TableRow sx={{ backgroundColor: "#f0f0f0" }}>
                                                        <TableCell colSpan={7}>
                                                            <Button
                                                                variant="text"
                                                                color="primary"
                                                                onClick={() => handleBuildingClick(matchedBuilding)}
                                                            >
                                                                <strong>{buildingName}</strong>
                                                            </Button>
                                                        </TableCell>
                                                    </TableRow>

                                                    {/* Month Breakdown Rows */}
                                                    {Object.entries(building.Building_by_month).map(([monthYear, counts]) => (
                                                        <TableRow key={`${building.building_unique_id}-${monthYear}`}>
                                                            <TableCell></TableCell> {/* Empty cell for spacing */}
                                                            <TableCell>{formatChartLabel(monthYear)}</TableCell>
                                                            <TableCell>{counts["Held and attended"]}</TableCell>
                                                            <TableCell>{counts["Held and attended - Corp"]}</TableCell>
                                                            <TableCell>{counts["Customer no show"]}</TableCell>
                                                            <TableCell>{counts["Scheduled"]}</TableCell>
                                                            <TableCell>{counts["Scheduled - Corp"]}</TableCell>
                                                        </TableRow>
                                                    ))}
                                                </React.Fragment>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Box>
                    )}

                    {selectedBuilding && (
                        <BuildingCheckInHistoryModal
                            open={openModal}
                            onClose={handleCloseModal}
                            building={selectedBuilding}
                        />
                    )}
                </Box>
            </Box>
        </Modal>

    );
};

export default MonthlyCheckinRunReport;
